import HoverDialog from '@app/src/components/HoverDialog'
import { FacetItem } from '@app/src/pages/ResourceCollection/index'
import { Box, Stack, Typography } from '@mui/material'
import ReactEcharts from 'echarts-for-react'
import React from 'react'
import { useIntl } from 'react-intl'
import useBaselineAssessmentOverviewGraph, {
  AssessmentOverViewGraphDatapoint,
} from './useBaselineAssessmentOverviewGraph'

export const handleFilterValue = (value: string | string[], newValue: string) => {
  if (!Array.isArray(value)) {
    return [newValue]
  }
  if (!value.includes(newValue)) {
    return [...value, newValue]
  }
  return value.filter(v => v !== newValue)
}

interface BaselineAssessmentOverviewGraphProps {
  graphLevels: Record<number, FacetItem | undefined>
  selectedLevels: number[]
  totalSuppliersThatResponded: number
  value: string | string[]
  onChange: (value: string | string[]) => void
  onPageChange: (page: number) => void
}

const BaselineAssessmentOverviewGraph: React.FC<BaselineAssessmentOverviewGraphProps> = ({
  graphLevels,
  selectedLevels,
  totalSuppliersThatResponded,
  value,
  onChange,
  onPageChange,
}) => {
  const { formatMessage } = useIntl()
  const [highlightedLevel, setHighlightedLevel] = React.useState<AssessmentOverViewGraphDatapoint>()
  const [offset, setOffset] = React.useState<number>(0)
  const chartRef = React.useRef<ReactEcharts>(null)

  const options = useBaselineAssessmentOverviewGraph(graphLevels, selectedLevels)

  React.useEffect(() => {
    if (chartRef.current) {
      const echartsInstance = chartRef.current.getEchartsInstance()
      if (echartsInstance) {
        const container = chartRef.current.ele
        if (container) {
          const containerWidth = container.offsetWidth
          echartsInstance.resize({ width: containerWidth, height: 56, silent: true })
        }
      }
    }
  }, [selectedLevels, options])

  const handleChartClick = React.useCallback(
    (params: { data: AssessmentOverViewGraphDatapoint } | AssessmentOverViewGraphDatapoint) => {
      const data = 'data' in params ? params.data : params
      if (!data || !data.name) return

      const level = data.name.match(/\d+/)
      if (!level) return
      const newValue = handleFilterValue(value, level[0])

      onChange(newValue)
      onPageChange(1)
    },
    [value, onChange, onPageChange],
  )

  const handleChartMouseover = React.useCallback(
    ({ data, event }: { data: AssessmentOverViewGraphDatapoint; event: { target: { shape: { x: number } } } }) => {
      setOffset(event.target.shape.x)
      setHighlightedLevel(data)
    },
    [],
  )

  const chartEvents = React.useMemo(
    () => ({
      mouseover: handleChartMouseover,
      click: handleChartClick,
    }),
    [handleChartMouseover, handleChartClick],
  )

  return (
    <HoverDialog
      placement="bottom-start"
      positionOffset={[offset, 0]}
      width={300}
      content={
        highlightedLevel && (
          <Stack spacing={1} alignItems="start">
            <Typography color="primary" variant="subtitle2">
              {highlightedLevel.name}
            </Typography>
            <Stack direction="row" spacing={4} alignItems="center" justifyContent="space-between">
              <Typography variant="body2">
                {formatMessage(
                  { id: 'baselineAssessment.percentOfAllSuppliers' },
                  {
                    count: highlightedLevel.value,
                    value: Math.round((highlightedLevel.value / totalSuppliersThatResponded) * 100),
                  },
                )}
              </Typography>
            </Stack>
          </Stack>
        )
      }
    >
      <Box width="100%" py={1} style={{ cursor: 'pointer' }}>
        <ReactEcharts ref={chartRef} option={options} style={{ height: 56, width: '100%' }} onEvents={chartEvents} />
      </Box>
    </HoverDialog>
  )
}

export default BaselineAssessmentOverviewGraph
